import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ChatShell, { Drawer } from "@/components/chat/ChatShell";
import ChatInterview from "@/components/chat/ChatInterview";
import { useLocale } from "@/lib/i18n";
import api from "@/lib/api";

export default function CaseChat() {
  const { id } = useParams();
  const { lang } = useLocale();
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [prog, setProg] = useState(null);
  const urdu = lang === "ur";

  const load = useCallback(() => { api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => setData(false)); }, [id]);
  useEffect(() => { setUnavailable(false); load(); }, [load]);
  const onUnavailable = useCallback(() => setUnavailable(true), []);

  if (data === false) return <ChatShell caseId={id}><p className="p-8 text-[#5E6282]">This application could not be loaded.</p></ChatShell>;
  if (!data) return <ChatShell caseId={id}><div className="m-6 h-40 animate-pulse rounded-lg bg-white" /></ChatShell>;

  const visa = data.visa_category_name;
  const dest = data.corridor?.destination;
  const mission = data.jurisdiction?.mission;
  const subtitle = `${dest} ${visa}${mission ? `, ${mission}` : ""}`;
  const list = (data.checklist || []).filter((r) => r.kind !== "info");

  return (
    <ChatShell caseId={id} subtitle={subtitle}>
      {prog && prog.text && !unavailable && (
        <div className="shrink-0 border-b border-[#E7E5DF] bg-white px-4 py-2" data-testid="chat-progress-bar">
          <div className="mx-auto max-w-3xl">
            <div className="mb-1 flex justify-between text-xs text-[#5E6282]" data-testid="interview-progress"><span>{prog.text}</span><span>{prog.section}</span></div>
            <div className="h-1 bg-[#E7E5DF]"><div className="h-1 bg-[#181E4B] transition-[width] duration-300" style={{ width: `${prog.pct}%` }} /></div>
          </div>
        </div>
      )}
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl space-y-4 px-4 py-6 pb-24">
          <div className="space-y-3 rounded-lg border border-[#E7E5DF] bg-white p-5 text-[#1E1D4C]" data-testid="assistant-welcome-bubble">
            <p className="font-semibold">{urdu ? "السلام علیکم! ہولاویسٹو میں خوش آمدید" : "Welcome to Holavisto."}</p>
            <p>
              {urdu
                ? `آپ نے ${dest} کے لیے ${visa} منتخب کیا ہے${mission ? `، جو ${mission} کے ذریعے دیکھا جاتا ہے` : ""}۔ مشن ایک مخصوص فہرست کی دستاویزات مانگتا ہے۔`
                : `You have selected ${dest}, ${visa}${mission ? `, handled by the ${mission}` : ""}. The mission asks for a specific list of documents.`}
            </p>
            <button data-testid="view-doc-list-link" onClick={() => setDocs(true)} className="font-medium text-[#029BC5] underline">{urdu ? "دستاویزات کی فہرست دیکھیں" : "View the document list"}</button>
            <p>{urdu ? "ہم تمام تیاری تیز ترین طریقے سے کروائیں گے۔ اس کے بعد آپ کی اپائنٹمنٹ ہم بک کریں گے۔" : "We will help you prepare everything in the fastest way possible. Once that is done, we book your appointment for you."}</p>
          </div>

          {unavailable ? (
            <div className="max-w-xl space-y-3 rounded-lg border border-[#E7E5DF] bg-white p-5 text-[#1E1D4C]" data-testid="interview-unavailable">
              <p>The guided questions for this visa are being finished. Our team will guide you on the next steps. Meanwhile you can prepare your forms in the full file.</p>
              <Link to={`/app/cases/${id}/file`} className="inline-block rounded-md bg-[#181E4B] px-5 py-2.5 text-sm font-semibold text-white" data-testid="open-full-file">Open my file</Link>
            </div>
          ) : (
            <ChatInterview caseId={id} onChanged={load} onUnavailable={onUnavailable} onProgress={setProg} />
          )}
        </div>
      </main>

      {docs && (
        <Drawer testid="doc-list-drawer" title={`Documents${mission ? `: ${mission}` : ""}`} sub={visa} onClose={() => setDocs(false)}>
          <ol className="divide-y divide-[#E7E5DF]">
            {list.map((r, i) => (
              <li key={r.code} className="flex gap-3 py-3" data-testid={`doc-item-${r.code}`}>
                <span className="w-5 pt-0.5 text-xs text-[#5E6282]">{i + 1}</span>
                <span>
                  <span className="block text-sm font-semibold text-[#1E1D4C]">{r.title}{!r.required && <span className="font-normal text-[#5E6282]"> (recommended)</span>}</span>
                  <span className="block text-xs text-[#5E6282]">{r.description}</span>
                  {r.copies && <span className="block text-xs text-[#5E6282]">Submit: {r.copies}</span>}
                </span>
              </li>
            ))}
          </ol>
          <p className="mt-4 text-xs text-[#5E6282]">Source: official mission pages. For information only. We prepare these with you step by step.</p>
        </Drawer>
      )}
    </ChatShell>
  );
}
